import React, {ReactNode} from "react";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import {ServerStatus, SupportEntryGroup} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {InputSwitch} from "primereact/inputswitch";
import SupportFields from "../dialog/field/SupportFields";
import Administration from "../session/Administration";
import {Calendar} from "primereact/calendar";
import {Button} from "primereact/button";
import DateHelper from "../../shared/helper/DateHelper";
import Downloader from "../../portal/document/Downloader";
import Translate from "../../shared/translation/Translate";
import Helper from "../../shared/components/Helper";
import FunctionHelper from "../../shared/helper/FunctionHelper";

export interface SupportPanelProperties extends AbstractPanelProperties {
}

export interface SupportPanelState extends AbstractPanelState {
	config: boolean;
	init: boolean;
	logs: boolean;
	startDate: Date;
	endDate: Date;
}

export class SupportPanelDefinition extends AbstractPanel<SupportPanelProperties, SupportPanelState> {
	constructor(props: Readonly<SupportPanelProperties>) {
		super(props);

		let startDate: Date = new Date();
		startDate.setDate(startDate.getDate() - 5);

		this.state = {
			...super.state,
			config: true,
			init: true,
			logs: true,
			startDate: startDate,
			endDate: DateHelper.currentDate()
		};
	}

	private onChange(field: SupportFields, value: any): void {
		switch (field) {
			case SupportFields.CONFIG:
				this.setState({
					config: value
				});
				break;
			case SupportFields.INIT:
				this.setState({
					init: value
				});
				break;
			case SupportFields.LOGS:
				this.setState({
					logs: value
				});
				break;
			case SupportFields.START:
				this.setState({
					startDate: value
				});
				break;
			case SupportFields.END:
				this.setState({
					endDate: value
				});
				break;
		}
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let downloader: Downloader = new Downloader();
		let administration: Administration = this.props.context.session.getAdministration();
		let groups: Array<SupportEntryGroup> = [];

		if (this.state.config) {
			groups.push(SupportEntryGroup.Config)
		}

		if (this.state.init) {
			groups.push(SupportEntryGroup.Init)
		}

		if (this.state.logs) {
			groups.push(SupportEntryGroup.Logs)
		}

		try {
			let data: Buffer = await administration.getSupport(
				groups,
				this.state.logs ? DateHelper.buildDateString(this.state.startDate) : undefined,
				this.state.logs ? DateHelper.buildDateString(this.state.endDate) : undefined
			);

			await downloader.download(
				new Blob([data]),
				"report-" + DateHelper.buildDateString(DateHelper.currentDate()) + ".zip"
			);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());

		if (!FunctionHelper.exists(serverStatus)) {
			return null;
		}

		let currentDate: Date = DateHelper.currentDate();

		return (
			<div className="screen admin-support-panel" data-help="global.screen.admin.content" data-position="top">
				<div className="body">
					<h1>
						<Translate {...this.props} data="adminscreen.panels.support.title" namespace="screen"/>
						{this.renderHelpButton()}
					</h1>
					<Helper {...this.props}>
						<Translate {...this.props} data={"global.screen.admin.support"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<div className="p-grid">
						<fieldset className="p-col-12 p-grid">
							<div className="p-col-12 p-xl-6 p-grid">
								<label className="p-col-12 p-sm-5">
									<Translate {...this.props} data="adminscreen.panels.support.group.label"
											   namespace="screen"/>
								</label>
								<div className="p-col-12 p-sm-7 switch-container">
									<InputSwitch checked={this.state.config} inputId="control-support-config"
												 disabled={this.state.config && !this.state.init && !this.state.logs}
												 onChange={(e) =>
													 this.onChange(SupportFields.CONFIG, e.value)
												 }/>
									<label className="switch-label" htmlFor="control-support-config">
										<Translate {...this.props} data="adminscreen.panels.support.group.config"
												   namespace="screen"/>
									</label>
								</div>

								<label className="p-col-12 p-sm-5"/>
								<div className="p-col-12 p-sm-7 switch-container">
									<InputSwitch checked={this.state.init} inputId="control-support-init"
												 disabled={!this.state.config && this.state.init && !this.state.logs}
												 onChange={(e) => this.onChange(SupportFields.INIT, e.value)}/>
									<label className="switch-label" htmlFor="control-support-init">
										<Translate {...this.props} data="adminscreen.panels.support.group.init"
												   namespace="screen"/>
									</label>
								</div>

								<label className="p-col-12 p-sm-5"/>
								<div className="p-col-12 p-sm-7 switch-container">
									<InputSwitch checked={this.state.logs} inputId="control-support-logs"
												 disabled={!this.state.config && !this.state.init && this.state.logs}
												 onChange={(e) => this.onChange(SupportFields.LOGS, e.value)}/>
									<label className="switch-label" htmlFor="control-support-logs">
										<Translate {...this.props} data="adminscreen.panels.support.group.logs"
												   namespace="screen"/>
									</label>
								</div>
							</div>
							<div className="p-col-12 p-xl-6 p-grid" hidden={!this.state.logs}>
								<label className="p-col-12 p-sm-5" htmlFor="control-support-start">
									<Translate {...this.props} data="adminscreen.panels.support.start"
											   namespace="screen"/>
								</label>
								<div className="input-group">
									<div className="p-inputgroup">
										<Calendar value={this.state.startDate} dateFormat="dd.mm.yy"
												  onChange={(e) => this.onChange(SupportFields.START, e.value)}
												  maxDate={this.state.endDate} inputId="control-support-start"
												  locale={this.props.language}/>
										<Button icon="icon icon-calendar"
												tooltip={Translate.value(
													"adminscreen.panels.logs.today", {ns: "screen"}
												)}
												onClick={() => this.onChange(SupportFields.START, this.state.endDate)}/>
									</div>
								</div>
								<label className="p-col-12 p-sm-5" htmlFor="control-support-end">
									<Translate {...this.props} data="adminscreen.panels.support.end"
											   namespace="screen"/>
								</label>
								<div className="input-group">
									<div className="p-inputgroup">
										<Calendar value={this.state.endDate} dateFormat="dd.mm.yy"
												  onChange={(e) =>
													  this.onChange(SupportFields.END, e.value)}
												  minDate={this.state.startDate} maxDate={currentDate}
												  locale={this.props.language} inputId="control-support-end"/>
										<Button icon="icon icon-calendar"
												tooltip={Translate.value(
													"adminscreen.panels.logs.today", {ns: "screen"}
												)}
												onClick={() => this.onChange(SupportFields.END, currentDate)}/>
									</div>
								</div>
							</div>
							<hr/>
							<div className="p-col-12 p-xl-6 p-grid">
								<label className="p-col-12 p-sm-5"/>
								<div className="p-col-12 p-sm-7">
									<Button label={Translate.value("buttons.download")}
											icon={"icon icon-download small"} className="primary-button download-button"
											onClick={() => this.submit()}/>
								</div>
							</div>
						</fieldset>
					</div>
				</div>
			</div>
		);
	}
}

const SupportPanel = React.memo(SupportPanelDefinition,
	(prevProps: Readonly<SupportPanelProperties>, nextProps: Readonly<SupportPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default SupportPanel;